import { Box } from "@mui/material";
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, useParams } from "react-router-dom";
import AppLayout from "../components/layout/AppLayout";
import SimplifiedAppLayout from "../components/layout/SimplifiedAppLayout";
import { PageType } from "../config/page.types";
import { BreadcrumbProvider } from "../context/BreadcrumbsProvider";
import { FiltersProvider } from "../context/FiltersProvider";
import { PageConfigurationProvider } from "../context/PageConfigrations/PageConfigurationProvider";
import CurrentUser from "../features/auth/CurrentUser";
import Protected from "../features/auth/Protected";
import ResetPassword from "../features/auth/ResetPassword";
import SignIn from "../features/auth/SignIn";
import DataInsightsDashboard from "../features/dataInsights/DataInsightsDashboard";
import DataInsightsDashboardDetail from "../features/dataInsights/DataInsightsDashboard/DataInsightsDashboardDetail";
import DataInsightsHotspots from "../features/dataInsights/DataInsightsHotspots";
import { RootCause } from "../features/dataInsights/DataInsightsRootCause";
import { RootCauseResults } from "../features/dataInsights/DataInsightsRootCause/RootCauseResults";
import DataIOAutomatedProcesses from "../features/dataIO/DataIOAutomatedProcesses";
import DataIODataExport from "../features/dataIO/DataIODataExport";
import { DataIOImport } from "../features/dataIO/DataIOImport";
import DataIOMonitor from "../features/dataIO/DataIOMonitor";
import KnowHowApiDocumentation from "../features/knowHow/KnowHowApiDocumentation";
import KnowHowKnowledgeBase from "../features/knowHow/KnowHowKnowledgeBase";
import KnowHowUserManuals from "../features/knowHow/KnowHowUserManuals";
import MasterDataAssumptions from "../features/masterData/MasterDataAssumptions";
import MasterDataConversionTables from "../features/masterData/MasterDataConversionTables";
import MasterDataFleetsVehicles from "../features/masterData/MasterDataFleetsVehicles";
import MasterDataNetworksRoutes from "../features/masterData/MasterDataNetworksRoutes";
import { MasterDataOperationalUnits } from "../features/masterData/MasterDataOperationalUnits";
import { MasterDataOperationalUnitsDetail } from "../features/masterData/MasterDataOperationalUnits/MasterDataOperationalUnitsDetail";
import PlatformSettingsAdmin from "../features/platformSettings/PlatformSettingsAdmin";
import PlatformSettingsApi from "../features/platformSettings/PlatformSettingsApi";
import PlatformSettingsDataDomains from "../features/platformSettings/PlatformSettingsDataDomains";
import OperationalUnits from "../features/results/ResultsOperationalUnits";
import OperationalUnitsGreenrouter from "../features/results/ResultsOperationalUnits/OperationalUnitsGreenrouter";
import OperationalUnitsGreenrouterDetail from "../features/results/ResultsOperationalUnits/OperationalUnitsGreenrouter/OperationalUnitsGreenrouterDetail";
import OperationalUnitsThirdParty from "../features/results/ResultsOperationalUnits/OperationalUnitsThirdParty";
import OperationalUnitsThirdPartyDetail from "../features/results/ResultsOperationalUnits/OperationalUnitsThirdParty/OperationalUnitsThirdPartyDetail";
import ResultsOverview from "../features/results/ResultsOverview";
import ResultsTransport from "../features/results/ResultsTransport";
import TransportGreenrouter from "../features/results/ResultsTransport/TransportGreenrouter";
import EntityDetails from "../features/results/ResultsTransport/TransportGreenrouter/EntityDetails";
import TransportGreenrouterDetail from "../features/results/ResultsTransport/TransportGreenrouter/TransportGreenrouterDetail";
import TransportThirdParty from "../features/results/ResultsTransport/TransportThirdParty";
import TransportThirdPartyDetail from "../features/results/ResultsTransport/TransportThirdParty/TransportThirdPartyDetail";
import ErrorView from "../features/service/ErrorView";
import WhatIfBudgeting from "../features/whatIf/WhatIfBudgeting";
import WhatIfCarbonSnap from "../features/whatIf/WhatIfCarbonSnap";
import CarbonSnapResults from "../features/whatIf/WhatIfCarbonSnap/CarbonSnapResults";
import WhatIfSimulations from "../features/whatIf/WhatIfSimulations";
import { BackToView, Http } from "./http";
import { AppRoutes } from "./routesUtils";

/**
 * The operational unit detail route also serves the creation form ("add-unit" id): the page to check
 * depends on the URL, so that viewing and creating units are protected separately.
 */
const ProtectedOperationalUnitDetail = () => {
	const { id } = useParams();
	const isCreation = AppRoutes.MasterDataOperationalUnitsAddUnit.endsWith(`/${id}`);

	return (
		<Protected
			pageTypes={[
				isCreation ? PageType.MasterDataOperationalUnitsAddUnit : PageType.MasterDataOperationalUnitsDetail,
			]}
		>
			<MasterDataOperationalUnitsDetail />
		</Protected>
	);
};

const homeRoute = createRoutesFromElements(
	<Route index id="home" path="/" element={<Navigate to={AppRoutes.ResultsOverview} />} />
);

// TODO: evaluate implementation of error boundaries (see.: https://github.com/bvaughn/react-error-boundary)
// Error boundaries may be used in conjunction with error pages for better error management.

const publicRoutes = createRoutesFromElements(
	<Route
		id="public-routes"
		element={
			<Box sx={{ display: "flex" }}>
				<SimplifiedAppLayout />
				<Box
					component="main"
					sx={{
						flex: 1,
						minWidth: 0, // flex 1 need this property to be able to resize in smaller size
						maxWidth: "100%",
						overflowX: "hidden",
					}}
				>
					<Outlet />
				</Box>
			</Box>
		}
	>
		{/* Sign in, unauthorized error */}
		<Route id="sign-in" path={AppRoutes.SignIn} element={<SignIn />} />
		<Route id="recover-password" path={AppRoutes.ResetPassword} element={<ResetPassword />} />
		<Route
			id="unauthorized"
			path={AppRoutes.Unauthorized}
			element={
				<PageConfigurationProvider>
					<ErrorView httpStatusCode={Http.Unauthorized} backToSection={BackToView.SignIn} />
				</PageConfigurationProvider>
			}
		/>
	</Route>
);

const protectedRoutes = createRoutesFromElements(
	<Route
		id="protected-routes"
		element={
			<PageConfigurationProvider>
				<FiltersProvider>
					<BreadcrumbProvider>
						<Box sx={{ display: "flex" }}>
							<AppLayout />
							<Box
								component="main"
								sx={{
									flex: 1,
									minWidth: 0, // flex 1 need this property to be able to resize in smaller size
									maxWidth: "100%",
									overflowX: "hidden",
								}}
							>
								<Outlet />
							</Box>
						</Box>
					</BreadcrumbProvider>
				</FiltersProvider>
			</PageConfigurationProvider>
		}
	>
		{/* Account: always accessible to authenticated users */}
		<Route
			path={AppRoutes.Account}
			element={
				<Protected>
					<CurrentUser />
				</Protected>
			}
		/>

		{/* Results */}
		<Route path={AppRoutes.Results} element={<Navigate to={AppRoutes.ResultsOverview} />} />

		<Route
			path={AppRoutes.ResultsOverview}
			element={
				<Protected pageTypes={[PageType.ResultsOverview]}>
					<ResultsOverview />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransport}
			element={
				<Protected pageTypes={[PageType.ResultsTransport]}>
					<ResultsTransport />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransportGreenrouter}
			element={
				<Protected
					pageTypes={[PageType.ResultsTransportGreenRouter, PageType.ResultsTransportGreenRouterEntities]}
				>
					<TransportGreenrouter />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransportGreenrouterDetail}
			element={
				<Protected pageTypes={[PageType.ResultsTransportGreenRouterDetail]}>
					<TransportGreenrouterDetail />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsEntityGreenRouterDetail}
			element={
				<Protected pageTypes={[PageType.ResultsTransportGreenRouterEntityDetail]}>
					<EntityDetails />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransportThirdParty}
			element={
				<Protected pageTypes={[PageType.ResultsTransportThirdParty]}>
					<TransportThirdParty />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransportThirdPartyDetail}
			element={
				<Protected pageTypes={[PageType.ResultsTransportThirdPartyDetail]}>
					<TransportThirdPartyDetail />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsOperationalUnits}
			element={
				<Protected pageTypes={[PageType.ResultsOperationalUnits]}>
					<OperationalUnits />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsOperationalUnitsGreenRouter}
			element={
				<Protected pageTypes={[PageType.ResultsOperationalUnitsGreenRouter]}>
					<OperationalUnitsGreenrouter />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultOperationalUnitsGreerouterNode}
			element={
				<Protected pageTypes={[PageType.ResultsOperationalUnitsGreenRouterDetail]}>
					<OperationalUnitsGreenrouterDetail />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsOperationalUnitsThirdParty}
			element={
				<Protected pageTypes={[PageType.ResultsOperationalUnitsThirdParty]}>
					<OperationalUnitsThirdParty />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsOperationalUnitsThirdPartyDetail}
			element={
				<Protected pageTypes={[PageType.ResultsOperationalUnitsThirdPartyDetail]}>
					<OperationalUnitsThirdPartyDetail />
				</Protected>
			}
		/>

		{/* Data Insights */}
		<Route path={AppRoutes.DataInsights} element={<Navigate to={AppRoutes.DataInsightsDashboardInsights} />} />

		<Route
			path={AppRoutes.DataInsightsDashboardInsights}
			element={
				<Protected pageTypes={[PageType.DataInsightsDashboard]}>
					<DataInsightsDashboard />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataInsightsDashboardInsightsDetail}
			element={
				<Protected pageTypes={[PageType.DataInsightsDashboardDetail]}>
					<DataInsightsDashboardDetail />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataInsightsHotspots}
			element={
				<Protected pageTypes={[PageType.DataInsightsHotspots]}>
					<DataInsightsHotspots />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataInsightsRootCause}
			element={
				<Protected pageTypes={[PageType.DataInsightsRootCause]}>
					<RootCause />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataInsightsRootCauseResults}
			element={
				<Protected pageTypes={[PageType.DataInsightsRootCauseResults]}>
					<RootCauseResults />
				</Protected>
			}
		/>

		{/* What If */}
		<Route path={AppRoutes.WhatIf} element={<Navigate to={AppRoutes.WhatIfCarbonSnap} />} />

		<Route
			path={AppRoutes.WhatIfSimulations}
			element={
				<Protected pageTypes={[PageType.WhatIfSimulations]}>
					<WhatIfSimulations />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.WhatIfBudgeting}
			element={
				<Protected pageTypes={[PageType.WhatIfBudgeting]}>
					<WhatIfBudgeting />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.WhatIfCarbonSnap}
			element={
				<Protected pageTypes={[PageType.WhatIfCarbonSnap]}>
					<WhatIfCarbonSnap />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.WhatIfCarbonSnapResults}
			element={
				<Protected pageTypes={[PageType.WhatIfCarbonSnapResults]}>
					<CarbonSnapResults />
				</Protected>
			}
		/>

		{/* Data I/O */}
		<Route path={AppRoutes.DataIO} element={<Navigate to={AppRoutes.DataIOMonitor} />} />

		<Route
			path={AppRoutes.DataIOMonitor}
			element={
				<Protected pageTypes={[PageType.DataIOMonitor]}>
					<DataIOMonitor />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataIOImport}
			element={
				<Protected pageTypes={[PageType.DataIOImport]}>
					<DataIOImport />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataIOAutomatedProcesses}
			element={
				<Protected pageTypes={[PageType.DataIOAutomatedProcesses]}>
					<DataIOAutomatedProcesses />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataIODataExport}
			element={
				<Protected pageTypes={[PageType.DataIODataExport]}>
					<DataIODataExport />
				</Protected>
			}
		/>

		{/* Master data & data configuration */}
		<Route path={AppRoutes.MasterData} element={<Navigate to={AppRoutes.MasterDataOperationalUnits} />} />

		<Route
			path={AppRoutes.MasterDataOperationalUnits}
			element={
				<Protected pageTypes={[PageType.MasterDataOperationalUnits]}>
					<MasterDataOperationalUnits />
				</Protected>
			}
		/>

		<Route path={AppRoutes.MasterDataOperationalUnitsDetail} element={<ProtectedOperationalUnitDetail />} />

		<Route
			path={AppRoutes.MasterDataAssumptions}
			element={
				<Protected pageTypes={[PageType.MasterDataAssumptions]}>
					<MasterDataAssumptions />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.MasterDataFleetsVehicles}
			element={
				<Protected pageTypes={[PageType.MasterDataFleetsVehicles]}>
					<MasterDataFleetsVehicles />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.MasterDataNetworksRoutes}
			element={
				<Protected pageTypes={[PageType.MasterDataNetworksRoutes]}>
					<MasterDataNetworksRoutes />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.MasterDataConversionTables}
			element={
				<Protected pageTypes={[PageType.MasterDataConversionTables]}>
					<MasterDataConversionTables />
				</Protected>
			}
		/>

		{/* Know how */}
		<Route path={AppRoutes.KnowHow} element={<Navigate to={AppRoutes.KnowHowUserManuals} />} />

		<Route
			path={AppRoutes.KnowHowUserManuals}
			element={
				<Protected pageTypes={[PageType.KnowHowUserManuals]}>
					<KnowHowUserManuals />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.KnowHowKnowledgeBase}
			element={
				<Protected pageTypes={[PageType.KnowHowKnowledgeBase]}>
					<KnowHowKnowledgeBase />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.KnowHowApiDocumentation}
			element={
				<Protected pageTypes={[PageType.KnowHowApiDocumentation]}>
					<KnowHowApiDocumentation />
				</Protected>
			}
		/>

		{/* Platform settings */}
		<Route path={AppRoutes.PlatformSettings} element={<Navigate to={AppRoutes.PlatformSettingsDataDomains} />} />

		<Route
			path={AppRoutes.PlatformSettingsDataDomains}
			element={
				<Protected pageTypes={[PageType.PlatformSettingsDataDomains]}>
					<PlatformSettingsDataDomains />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.PlatformSettingsAdmin}
			element={
				<Protected pageTypes={[PageType.PlatformSettingsAdmin]}>
					<PlatformSettingsAdmin />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.PlatformSettingsApi}
			element={
				<Protected pageTypes={[PageType.PlatformSettingsApi]}>
					<PlatformSettingsApi />
				</Protected>
			}
		/>

		{/* Forbidden error: rendered with side menu, unauthenticated users are redirected to sign in */}
		<Route
			id="forbidden"
			path={AppRoutes.Forbidden}
			element={
				<Protected>
					<ErrorView httpStatusCode={Http.Forbidden} backToSection={BackToView.HomeOrSignIn} />
				</Protected>
			}
		/>

		{/* Not found error: rendered with side menu, unauthenticated users are redirected to sign in */}
		<Route
			id="not-found"
			path="*"
			element={
				<Protected>
					<ErrorView httpStatusCode={Http.NotFound} backToSection={BackToView.Home} />
				</Protected>
			}
		/>
	</Route>
);

/**
 * Exports registered (active) application routes.
 */
export const applicationRoutes = createBrowserRouter([...homeRoute, ...publicRoutes, ...protectedRoutes]);
